import { apiClient } from "./client";

export interface CentroAssistenzaOption {
  id: string;
  nome: string;
  abbreviazione: string | null;
}

export interface IstitutoVigilanzaOption {
  id: string;
  nome: string;
  zona: string | null;
}

// Tipi completi — usati dalla pagina di gestione (creazione/modifica), a
// differenza delle Option minime sopra usate ovunque altrove per le select.
export interface CentroAssistenza extends CentroAssistenzaOption {
  email: string | null;
  telefono: string | null;
  indirizzo: string | null;
  comune: string | null;
  provincia: string | null;
  cap: string | null;
  partita_iva: string | null;
  note: string | null;
  /// Usate come punto di partenza per il calcolo dei percorsi (vedi api/routing.ts).
  lat: number | null;
  lng: number | null;
  geocoded_at: string | null;
}

/** Le due capacita' di un operatore logistico, cioe' i due elenchi della UI. */
export type CapacitaOperatore = "trasporto_valori" | "sala_conta";

export interface OperatoreLogisticoOption {
  id: string;
  nome: string;
  zona: string | null;
}

export interface OperatoreLogistico extends OperatoreLogisticoOption {
  fa_trasporto_valori: boolean;
  fa_sala_conta: boolean;
  email: string | null;
  telefono: string | null;
  indirizzo: string | null;
  comune: string | null;
  provincia: string | null;
  cap: string | null;
  partita_iva: string | null;
  note: string | null;
}

export interface IstitutoVigilanza extends IstitutoVigilanzaOption {
  email: string | null;
  telefono: string | null;
  indirizzo: string | null;
  comune: string | null;
  provincia: string | null;
  cap: string | null;
  partita_iva: string | null;
  note: string | null;
}

export async function listCentriAssistenza(): Promise<{ centri: CentroAssistenza[] }> {
  const { data } = await apiClient.get<{ centri: CentroAssistenza[] }>("/centri-assistenza");
  return data;
}

export async function createCentroAssistenza(payload: Partial<CentroAssistenza>): Promise<{ centro: CentroAssistenza }> {
  const { data } = await apiClient.post<{ centro: CentroAssistenza }>("/centri-assistenza", payload);
  return data;
}

export async function updateCentroAssistenza(
  id: string,
  payload: Partial<CentroAssistenza>
): Promise<{ centro: CentroAssistenza }> {
  const { data } = await apiClient.patch<{ centro: CentroAssistenza }>(`/centri-assistenza/${id}`, payload);
  return data;
}

export async function deleteCentroAssistenza(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/centri-assistenza/${id}`);
  return data;
}

export async function geocodeCentroAssistenza(id: string): Promise<{ centro: CentroAssistenza }> {
  const { data } = await apiClient.post<{ centro: CentroAssistenza }>(`/centri-assistenza/${id}/geocode`);
  return data;
}

export async function listIstitutiVigilanza(): Promise<{ istituti: IstitutoVigilanza[] }> {
  const { data } = await apiClient.get<{ istituti: IstitutoVigilanza[] }>("/istituti-vigilanza");
  return data;
}

export async function createIstitutoVigilanza(
  payload: Partial<IstitutoVigilanza>
): Promise<{ istituto: IstitutoVigilanza }> {
  const { data } = await apiClient.post<{ istituto: IstitutoVigilanza }>("/istituti-vigilanza", payload);
  return data;
}

export async function updateIstitutoVigilanza(
  id: string,
  payload: Partial<IstitutoVigilanza>
): Promise<{ istituto: IstitutoVigilanza }> {
  const { data } = await apiClient.patch<{ istituto: IstitutoVigilanza }>(`/istituti-vigilanza/${id}`, payload);
  return data;
}

/**
 * Elenco operatori logistici. Senza `capacita` torna l'anagrafica completa
 * (la pagina di gestione); con `capacita` torna il singolo elenco, che e'
 * quello che serve alle select del form device.
 */
export async function listOperatoriLogistici(
  capacita?: CapacitaOperatore
): Promise<{ operatori: OperatoreLogistico[] }> {
  const { data } = await apiClient.get<{ operatori: OperatoreLogistico[] }>("/operatori-logistici", {
    params: capacita ? { capacita } : undefined,
  });
  return data;
}

export async function createOperatoreLogistico(
  payload: Partial<OperatoreLogistico>
): Promise<{ operatore: OperatoreLogistico }> {
  const { data } = await apiClient.post<{ operatore: OperatoreLogistico }>("/operatori-logistici", payload);
  return data;
}

export async function updateOperatoreLogistico(
  id: string,
  payload: Partial<OperatoreLogistico>
): Promise<{ operatore: OperatoreLogistico }> {
  const { data } = await apiClient.patch<{ operatore: OperatoreLogistico }>(`/operatori-logistici/${id}`, payload);
  return data;
}

export async function deleteOperatoreLogistico(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/operatori-logistici/${id}`);
  return data;
}

export async function deleteIstitutoVigilanza(id: string): Promise<{ success: true }> {
  const { data } = await apiClient.delete<{ success: true }>(`/istituti-vigilanza/${id}`);
  return data;
}

export interface SedeSuggestion {
  id: string;
  nome_sede: string | null;
  indirizzo: string | null;
  citta: string | null;
  provincia: string | null;
  cliente_id: string;
  /** Ragione sociale dell'azienda madre. */
  cliente_nome: string;
  /** Marchio con cui il punto vendita si presenta. `null` per chi non ne ha. */
  cliente_insegna: string | null;
}

export async function searchSedi(q: string): Promise<{ sedi: SedeSuggestion[] }> {
  const { data } = await apiClient.get<{ sedi: SedeSuggestion[] }>("/sedi-search", { params: { q } });
  return data;
}
